import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Edit2, Trash2, Copy, Send } from 'lucide-react';
import templatesAPI from '../api/templates';
import CampaignDrawer from '../components/CampaignDrawer.jsx';
import '../styles/TemplatesPage.css';

const TemplatesPage = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [starterTemplates, setStarterTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('starters');
  const [duplicating, setDuplicating] = useState(false);
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [campaignTemplate, setCampaignTemplate] = useState(null);

  useEffect(() => {
    loadTemplates();
    loadStarterTemplates();
  }, []);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const response = await templatesAPI.getAll();
      // Platform API returns { success, templates: [], count: N }
      setTemplates(response.templates || []);
      setStarterTemplates(response.templates || []); // All platform templates are "starters"
    } catch (error) {
      console.error('Failed to load templates:', error);
      setTemplates([]);
      setStarterTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const loadStarterTemplates = async () => {
    // For platform emails, all templates are system templates
    // This is handled in loadTemplates()
  };

  const handleEdit = (e, templateId) => {
    e.stopPropagation();
    navigate(`/editor/${templateId}`);
  };

  const handleDelete = async (e, templateId, templateName) => {
    e.stopPropagation();

    if (!window.confirm(`Are you sure you want to delete "${templateName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await templatesAPI.delete(templateId);
      alert('Template deleted successfully');
      loadTemplates(); // Reload list
    } catch (error) {
      console.error('Failed to delete template:', error);
      alert('Failed to delete template: ' + error.message);
    }
  };

  const toggleSelectionMode = () => {
    setIsSelectionMode(!isSelectionMode);
    setSelectedTemplates([]);
  };

  const toggleTemplateSelection = (templateId) => {
    setSelectedTemplates(prev =>
      prev.includes(templateId)
        ? prev.filter(id => id !== templateId)
        : [...prev, templateId]
    );
  };

  const handleBulkDelete = async () => {
    if (selectedTemplates.length === 0) {
      alert('Please select templates to delete');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedTemplates.length} template(s)? This action cannot be undone.`)) {
      return;
    }

    try {
      await Promise.all(selectedTemplates.map(id => templatesAPI.delete(id)));
      alert(`Successfully deleted ${selectedTemplates.length} template(s)`);
      setSelectedTemplates([]);
      setIsSelectionMode(false);
      loadTemplates();
    } catch (error) {
      console.error('Failed to delete templates:', error);
      alert('Failed to delete some templates: ' + error.message);
    }
  };

  const handleUseStarter = async (e, templateId, templateName) => {
    e.stopPropagation();

    // Prevent duplicate clicks
    if (duplicating) {
      return;
    }

    setDuplicating(true);

    try {
      const response = await templatesAPI.duplicate(templateId, `${templateName} (Copy)`);

      const newTemplateId = response.data?.template?._id || response.data?._id;

      if (newTemplateId) {
        navigate(`/editor/${newTemplateId}`);
      } else {
        console.error('No template ID in response:', response);
        alert('Failed to get new template ID');
      }
    } catch (error) {
      console.error('Failed to use starter template:', error);
      alert('Failed to use template: ' + error.message);
    } finally {
      setDuplicating(false);
    }
  };

  const displayedTemplates = activeTab === 'starters'
    ? (Array.isArray(starterTemplates) ? starterTemplates : [])
    : (Array.isArray(templates) ? templates : []);

  return (
    <div className="templates-page">
      <div className="page-header">
        <h1>Email Templates</h1>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          {activeTab === 'my-templates' && (
            <>
              <button
                onClick={toggleSelectionMode}
                className="btn-secondary"
                style={{
                  background: isSelectionMode ? '#00D9FF' : 'transparent',
                  border: '1px solid #00D9FF',
                  color: isSelectionMode ? '#000' : '#00D9FF',
                }}
              >
                {isSelectionMode ? 'Cancel Selection' : 'Select Templates'}
              </button>
              {isSelectionMode && selectedTemplates.length > 0 && (
                <button
                  onClick={handleBulkDelete}
                  className="btn-danger"
                  style={{
                    background: 'linear-gradient(135deg, #FF1493 0%, #DC143C 100%)',
                    color: 'white',
                    border: 'none',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '0.5rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                  }}
                >
                  Delete Selected ({selectedTemplates.length})
                </button>
              )}
            </>
          )}
          <button
            onClick={() => navigate('/editor')}
            className="btn-primary"
          >
            + Create New Template
          </button>
        </div>
      </div>

      <div className="tabs">
        <button
          className={`tab ${activeTab === 'starters' ? 'tab-active' : ''}`}
          onClick={() => setActiveTab('starters')}
        >
          Starter Templates ({starterTemplates.length})
        </button>
        <button
          className={`tab ${activeTab === 'my-templates' ? 'tab-active' : ''}`}
          onClick={() => setActiveTab('my-templates')}
        >
          My Templates ({templates.length})
        </button>
      </div>

      {duplicating && (
        <div className="loading-overlay">
          <div className="loading-message">Creating your template...</div>
        </div>
      )}

      {loading ? (
        <div className="loading">Loading templates...</div>
      ) : (
        <div className="templates-grid">
          {displayedTemplates.length === 0 ? (
            <div className="empty-state">
              <p>{activeTab === 'starters' ? 'No starter templates available' : 'No templates yet'}</p>
              <p>{activeTab === 'starters' ? 'Check back soon for pre-built templates' : 'Create your first email template to get started'}</p>
            </div>
          ) : (
            displayedTemplates.map((template) => (
              <div
                key={template._id}
                className={`template-card ${duplicating ? 'disabled' : ''} ${isSelectionMode && selectedTemplates.includes(template._id) ? 'selected' : ''}`}
                onClick={() => {
                  if (duplicating) return;
                  if (isSelectionMode && !template.isStarter) {
                    toggleTemplateSelection(template._id);
                  } else if (template.isStarter) {
                    handleUseStarter({ stopPropagation: () => {} }, template._id, template.name);
                  } else {
                    navigate(`/editor/${template._id}`);
                  }
                }}
                style={{
                  cursor: duplicating ? 'not-allowed' : 'pointer',
                  opacity: duplicating ? 0.6 : 1,
                  border: isSelectionMode && selectedTemplates.includes(template._id) ? '2px solid #00D9FF' : undefined,
                }}
              >
                <div className="template-card-header">
                  {isSelectionMode && !template.isStarter && (
                    <input
                      type="checkbox"
                      checked={selectedTemplates.includes(template._id)}
                      onChange={(e) => {
                        e.stopPropagation();
                        toggleTemplateSelection(template._id);
                      }}
                      style={{
                        width: '20px',
                        height: '20px',
                        cursor: 'pointer',
                        accentColor: '#00D9FF',
                      }}
                    />
                  )}
                  <h3 style={{ flex: 1 }}>{template.name}</h3>
                  <div className="template-card-actions">
                    {template.isStarter ? (
                      <>
                        <button
                          onClick={(e) => { e.stopPropagation(); setCampaignTemplate(template); }}
                          className="icon-btn icon-btn-primary"
                          title="Send campaign"
                          style={{ color: '#FF1493' }}
                        >
                          <Send size={16} />
                        </button>
                        <button
                          onClick={(e) => handleUseStarter(e, template._id, template.name)}
                          className="icon-btn icon-btn-primary"
                          title="Use this template"
                        >
                          <Copy size={16} />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={(e) => { e.stopPropagation(); setCampaignTemplate(template); }}
                          className="icon-btn"
                          title="Send campaign"
                          style={{ color: '#FF1493' }}
                        >
                          <Send size={16} />
                        </button>
                        <button
                          onClick={(e) => handleEdit(e, template._id)}
                          className="icon-btn"
                          title="Edit template"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={(e) => handleDelete(e, template._id, template.name)}
                          className="icon-btn icon-btn-danger"
                          title="Delete template"
                        >
                          <Trash2 size={16} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <p className="template-card-description">
                  {template.description || 'No description provided'}
                </p>
                {template.isStarter && (
                  <p className="template-card-hint">
                    Click to use this template →
                  </p>
                )}
                <div className="template-card-meta">
                  <span className="badge">{template.category}</span>
                  <span className="template-card-blocks">
                    {template.blocks?.length || 0} blocks
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                  {template.isStarter && (
                    <span className="badge badge-info">✨ Starter Template</span>
                  )}
                  {template.isPublished && (
                    <span className="badge badge-success">✓ Published to SES</span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {campaignTemplate && (
        <CampaignDrawer
          template={campaignTemplate}
          onClose={() => setCampaignTemplate(null)}
          onSent={() => setCampaignTemplate(null)}
        />
      )}
    </div>
  );
};

export default TemplatesPage;
